import React from 'react';

import { Document } from '@contentful/rich-text-types';
import { Box, Col, getCSSVariable, Image, Link, Row, Text } from '@opendoor/bricks-next';
import LazyLoad from 'components/helpers/LazyLoad';
import { ISiteFooter } from 'declarations/contentful';

import {
  BNColorThemes,
  getBNComponentThemeColors,
  IBNComponentThemeConfig,
} from '../styles/ComponentTheme';
import FooterAppDownload from './FooterAppDownload';
import FooterBrand from './FooterBrand';
import FooterHelpCenter from './FooterHelpCenter';
import FooterLegal from './FooterLegal';
import FooterNav from './FooterNav';

const DEFAULT_APPLE_STORE_LINK =
  'https://apps.apple.com/us/app/opendoor-buy-sell-homes/id1143708068';
const DEFAULT_GOOGLE_PLAY_LINK =
  'https://play.google.com/store/apps/details?id=com.opendoor.buyerapp&hl=en';
const DEFAULT_HELP_CENTER_LINK = 'https://help.opendoor.com/';

export interface IFooterProps {
  siteFooter?: ISiteFooter;
  extraLegalText?: Document;
  showCustomerTestimonialLegal?: boolean;
  showEligibilityLegal?: boolean;
  showPromotionLegal?: boolean;
  showThePdpCopyrightLegal?: boolean;
}

const FooterSellInYourMarket = ({
  sellInYourMarket,
  theme,
}: {
  sellInYourMarket?: { label: string; path: string }[];
  theme: IBNComponentThemeConfig;
}) => {
  if (!sellInYourMarket?.length) {
    return null;
  }

  const sortedMarkets = [...sellInYourMarket].sort((a, b) => a.label.localeCompare(b.label));

  return (
    <Col
      width="100%"
      padding="$4"
      alignItems="flex-start"
      backgroundColor={theme.bgPrimaryLight}
      borderRadius="$6x"
      gap="$4"
      overflow="hidden"
    >
      <Text
        tag="p"
        minWidth="$20"
        flexShrink={0}
        typography="$labelSmall"
        color={theme.contentPrimary}
      >
        Learn about selling in your market
      </Text>
      <Row gap="$2" flexWrap="wrap" alignItems="flex-end" flex={1} minWidth={0}>
        {sortedMarkets.map((market, i) => (
          <React.Fragment key={market.path}>
            <Box tag="li">
              <Link
                analyticsName={`footer-link-column-${market.label}`}
                typography="$bodyMedium"
                href={`/sell/${market.path}`}
                variant="secondaryInverse"
                textDecorationLine="none"
              >
                {market.label}
              </Link>
            </Box>
            {i < sortedMarkets.length - 1 && <Box tag="span">|</Box>}
          </React.Fragment>
        ))}
      </Row>
    </Col>
  );
};

export const FooterForegroundBox = ({
  children,
  title,
  theme,
}: {
  children: React.ReactNode;
  title: string;
  theme: IBNComponentThemeConfig;
  isAppButtonFooter?: boolean;
}) => {
  return (
    <Box
      flex={1}
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      $largerThanSM={{ flexDirection: 'row', justifyContent: 'space-between' }}
      width="100%"
      padding="$4"
      gap="$2"
      backgroundColor={theme.bgPrimaryLight}
      borderRadius="$6x"
    >
      <Text tag="p" flexShrink={0} typography="$labelSmall" color={theme.contentPrimary}>
        {title}
      </Text>
      {children}
    </Box>
  );
};

const Footer = ({
  siteFooter,
  extraLegalText,
  showCustomerTestimonialLegal,
  showEligibilityLegal,
  showPromotionLegal,
  showThePdpCopyrightLegal,
}: IFooterProps) => {
  const footerTitle = siteFooter?.fields.footerTitle;
  const footerNav = siteFooter?.fields.footerNav;
  const sellInYourMarket = siteFooter?.fields.sellInYourMarket as
    | { label: string; path: string }[]
    | undefined;
  const googlePlaystoreLink = siteFooter?.fields.googlePlaystoreLink ?? DEFAULT_GOOGLE_PLAY_LINK;
  const appleStoreLink = siteFooter?.fields.appleStoreLink ?? DEFAULT_APPLE_STORE_LINK;
  const helpCenterLink = siteFooter?.fields.helpCenterLink ?? DEFAULT_HELP_CENTER_LINK;
  const backgroundColor = siteFooter?.fields.backgroundColor;
  const locale = siteFooter?.sys.locale;
  const aiPrompt = siteFooter?.fields.aiPrompt;

  const theme = getBNComponentThemeColors(
    backgroundColor && backgroundColor in BNColorThemes
      ? (backgroundColor as BNColorThemes)
      : BNColorThemes.Grey,
  );
  const appleStoreImageUrl =
    locale === 'es'
      ? 'https://images.opendoor.com/source/s3/imgdrop-production/2025-06-1750894044413-61865.png?service=cosmos'
      : 'https://images.opendoor.com/source/s3/imgdrop-production/2023-12-1702502078078-95093.png?service=cosmos';
  const googlePlayStoreImageUrl =
    locale === 'es'
      ? 'https://images.opendoor.com/source/s3/imgdrop-production/2025-06-1750894682317-95550.png?service=cosmos'
      : 'https://images.opendoor.com/source/s3/imgdrop-production/2023-12-1702502078081-28903.png?service=cosmos';

  return (
    <Col
      tag="footer"
      backgroundColor={theme.bgPrimary}
      // @ts-expect-error theme token type mismatch with getCSSVariable
      style={{ color: getCSSVariable(theme.contentPrimary) }}
      minHeight="$113"
    >
      <LazyLoad>
        <Box>
          <Box
            display="block"
            maxWidth="$360"
            mx="auto"
            p="$5"
            pt="$22"
            $largerThanSM={{ p: '$20' }}
            width="100%"
          >
            <Box
              gap="$16"
              maxWidth="100vw"
              $largerThanMD={{ flexDirection: 'row' }}
              flexDirection="column"
            >
              <FooterBrand footerTitle={footerTitle} theme={theme} aiPrompt={aiPrompt} />
              <Col gap="$4" flex={1}>
                <FooterNav footerNav={footerNav} theme={theme} />
                <FooterSellInYourMarket sellInYourMarket={sellInYourMarket} theme={theme} />
                <Box $largerThanLG={{ flexDirection: 'row' }} gap="$4">
                  <FooterAppDownload
                    theme={theme}
                    appleStoreLink={appleStoreLink}
                    appleStoreImageUrl={appleStoreImageUrl}
                    googlePlaystoreLink={googlePlaystoreLink}
                    googlePlayStoreImageUrl={googlePlayStoreImageUrl}
                    locale={locale}
                  />
                  <FooterHelpCenter theme={theme} helpCenterLink={helpCenterLink} />
                </Box>

                <FooterLegal
                  theme={theme}
                  extraLegalText={extraLegalText}
                  showCustomerTestimonialLegal={showCustomerTestimonialLegal}
                  showEligibilityLegal={showEligibilityLegal}
                  showPromotionLegal={showPromotionLegal}
                  showThePdpCopyrightLegal={showThePdpCopyrightLegal}
                />

                {/* Copyright + year section */}
                <Row pt="$16" alignItems="center" gap="$4" flexWrap="wrap">
                  <Text tag="span" typography="$bodyMedium" color={theme.contentPrimary}>
                    &copy; {new Date().getFullYear()} Opendoor | All rights reserved.
                  </Text>
                  <Row
                    gap="$4"
                    alignItems="center"
                    // @ts-expect-error theme token type mismatch with getCSSVariable
                    style={{ color: getCSSVariable(theme.contentPrimary) }}
                  >
                    <Image
                      source={{
                        uri: 'https://images.opendoor.com/source/s3/imgdrop-production/f2398a1e83a24962a9297d13f2ac6ea5.png?preset=square-2048',
                      }}
                      alt="Equal Housing Opportunity"
                      width={38}
                      height={38}
                    />
                    <Link
                      href="https://www.bbb.org/us/az/tempe/profile/real-estate-services/opendoor-1126-1000060421/#sealclick"
                      analyticsName="cosmos-landing-page-v2-footer-bbb-link"
                      aria-label="Better Business Bureau accredited since June 8th 2021 link"
                      hoverStyle={{ color: theme.contentPrimary }}
                      color={theme.contentPrimary}
                      textDecorationLine="underline"
                    >
                      <Image
                        source={{
                          uri: 'https://images.opendoor.com/source/s3/imgdrop-production/1cd65eee0b8345689ceff0292986f125.png?preset=square-2048',
                        }}
                        alt="Better Business Bureau"
                        width={105}
                        height={38}
                      />
                    </Link>
                  </Row>
                </Row>
              </Col>
            </Box>
          </Box>
        </Box>
      </LazyLoad>
    </Col>
  );
};

export default Footer;
